import { AtsCandidateFormVisibilityTypeEnum } from '@crehana/compass.knowledgehub/types';

import { Button } from '@crehana/compass.ui/button';
import { toast } from '@crehana/compass.ui/toaster';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@crehana/forms/components';
import { FormProvider, useForm, zodResolver } from '@crehana/forms/rhf';
import { useEffect, useState } from 'react';
import type { TPageLoaderVacancyData } from '../../types';
import { useCreateJobApplicantProfile } from '../hooks/create-job-applicant-profile';
import { useTrackVacancyVisit } from '../hooks/use-track-vacancy-visit';
import { useTranslationCandidateForm } from '../hooks/use-translations';
import type { TCandidateForm } from '../types';
import { EmailValidationPopUp } from './email-validation-pop-up';
import { GeneralInformation } from './general-information';
import { LinkedInValidationPopUp } from './linkedin-validation-pop-up';
import { Questions } from './questions';
import { candidateFormSchema } from './schemas';

type TFormProps = {
  jobVacancyPublicInfo: TPageLoaderVacancyData;
  setIsPublished?: React.Dispatch<React.SetStateAction<boolean>>;
  organizationId: number;
  preview: boolean;
};

export const Form = ({
  jobVacancyPublicInfo,
  setIsPublished,
  organizationId,
  preview = false,
}: TFormProps) => {
  const { t } = useTranslationCandidateForm();
  //Pop up to validate the email
  const [openEmailValidation, setOpenEmailValidation] = useState(false);
  //Pop up to validate the linkedin url
  const [openLinkedInValidation, setOpenLinkedInValidation] = useState(false);
  //Get the personal info map
  const personalInfoMap = Object.fromEntries(
    jobVacancyPublicInfo?.personal_info?.map(item => [
      item.label,
      item.visibility,
    ]) || [],
  );

  const [trackVacancyVisit] = useTrackVacancyVisit();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!jobVacancyPublicInfo.id || !organizationId) {
      return;
    }

    trackVacancyVisit({
      variables: {
        input: {
          vacancy_id: jobVacancyPublicInfo.id.toString(),
          centralized_organization_id: organizationId.toString(),
        },
      },
    });
  }, [jobVacancyPublicInfo.id, organizationId]);

  //Create the form
  const form = useForm<TCandidateForm>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      last_name: '',
      email: '',
      number_phone: '+',
      country: '',
      linkedin_url: null,
      resume: '',
      candidate_source: undefined,
      disability: undefined,
      gender: undefined,
      answers:
        jobVacancyPublicInfo?.questions?.map(q => ({
          question_id: Number(q.id),
          question_type: q.question_type,
          question_name: q.title,
          allow_multiple_responses: q.allow_multiple_responses,
          question_options: q.question_options || undefined,
          answer_value: {
            value: '',
            is_required: q.is_required,
            response_size: q.response_size || undefined,
          },
        })) || [],
    },
    resolver: zodResolver(
      candidateFormSchema({
        number_phone:
          personalInfoMap['PERSONAL_INFO_PHONE'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
        country:
          personalInfoMap['PERSONAL_INFO_COUNTRY'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
        resume:
          personalInfoMap['PERSONAL_INFO_RESUME'] ===
            AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility ||
          personalInfoMap['PERSONAL_INFO_RESUME'] ===
            AtsCandidateFormVisibilityTypeEnum.AlwaysRequiredCandidateFormVisibility,
        linkedin_url:
          personalInfoMap['PERSONAL_INFO_LINKEDIN'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
        candidate_source:
          personalInfoMap['PERSONAL_INFO_CANDIDATE_SOURCE'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
        disability:
          personalInfoMap['PERSONAL_INFO_DISABILITY'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
        gender:
          personalInfoMap['PERSONAL_INFO_DIVERSITY_GENDER'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
      }),
    ),
  });

  const { control } = form;
  const [createJobApplicantProfile, { loading }] =
    useCreateJobApplicantProfile();

  const onSubmit = async (data: TCandidateForm) => {
    // Validate phone number length (min 5, max 15) before submitting
    const phoneNumber =
      data.number_phone?.replace(/^\+/, '').replace(/\D/g, '') || '';
    if (phoneNumber && (phoneNumber.length < 5 || phoneNumber.length > 15)) {
      toast.error(t('INVALID_PHONE_LENGTH'));
      return;
    }

    try {
      const result = await createJobApplicantProfile({
        variables: {
          input: {
            vacancy_id: Number(jobVacancyPublicInfo.id),
            first_name: data.name,
            last_name: data.last_name,
            email: data.email,
            phone: data.number_phone,
            country: data.country,
            file_cv_id: parseInt(data.resume, 10),
            linkedin_url:
              personalInfoMap['PERSONAL_INFO_LINKEDIN'] ===
              AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility
                ? null
                : data.linkedin_url,
            origin_candidates:
              personalInfoMap['PERSONAL_INFO_CANDIDATE_SOURCE'] ===
              AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility
                ? null
                : data.candidate_source,
            disability:
              personalInfoMap['PERSONAL_INFO_DISABILITY'] ===
              AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility
                ? null
                : data.disability,
            gender:
              personalInfoMap['PERSONAL_INFO_DIVERSITY_GENDER'] ===
              AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility
                ? null
                : data.gender,
            answers: data.answers.map(answer => ({
              answer_value: answer.answer_value.value,
              question_id: answer.question_id,
              question_name: answer.question_name,
              question_type: answer.question_type,
            })),
          },
        },
      });

      if (
        result.data?.ats_recruitment.create_job_applicant_profile
          ?.__typename === 'CreateJobApplicantProfileSuccess'
      ) {
        setIsPublished?.(true);
        return;
      }
      if (
        result.data?.ats_recruitment.create_job_applicant_profile
          ?.__typename === 'MutationApplicantEmailExistsError'
      ) {
        setOpenEmailValidation(true);
        return;
      }
      if (
        result.data?.ats_recruitment.create_job_applicant_profile
          ?.__typename === 'MutationApplicantInvalidLinkedinError'
      ) {
        setOpenLinkedInValidation(true);
        return;
      }
      if (
        result.data?.ats_recruitment.create_job_applicant_profile
          ?.__typename === 'MutationValidationExistsError'
      ) {
        toast.error(t('VALIDATION_ERROR'));
        return;
      }

      toast.error(t('ERROR_TO_CREATE_JOB_APPLICANT_PROFILE'));
    } catch (error) {
      console.error('Error to create a job applicant profile', error);
      toast.error(t('ERROR_TO_CREATE_JOB_APPLICANT_PROFILE'));
    }
  };

  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-text-center">
      <p className="tw-font-label2">{t('APPLICATION_FORM')}</p>
      {/* Job vacancy title */}
      <h3 className="tw-font-h3 tw-font-semibold tw-mb-5.6">
        {jobVacancyPublicInfo?.title}
      </h3>
      <div className="tw-flex tw-text-start tw-flex-col">
        <p className="tw-font-body4 tw-text-neutral-light-700">
          {t('REQUIRED_FIELDS')}
          <span className="tw-text-alert-500">{t('REQUIRED_ICON')}</span>
        </p>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            {/* General information */}
            <GeneralInformation
              jobVacancyPublicInfo={jobVacancyPublicInfo}
              organizationId={organizationId || 0}
              preview={preview}
            />
            {/* Questions */}
            {form
              .watch('answers')
              .sort((a, b) => a.question_id - b.question_id)
              .map((question, index) => (
                <FormField
                  key={question.question_id}
                  control={control}
                  name={`answers.${index}.answer_value`}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem className="tw-mt-3.2">
                      <FormLabel className="tw-font-subtitle4">
                        {question.question_name.replace(/\*$/, '')}
                        {question.answer_value.is_required && (
                          <span className="tw-text-alert-500">
                            {t('REQUIRED_ICON')}
                          </span>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Questions
                          question_id={question.question_id.toString()}
                          type={question.question_type}
                          value={field.value.value}
                          onChange={val => {
                            field.onChange({
                              ...field.value,
                              value: val,
                              is_required: question.answer_value.is_required,
                              response_size:
                                question.answer_value.response_size,
                            });
                          }}
                          response_size={question.answer_value.response_size}
                          allow_multiple_responses={
                            question.allow_multiple_responses
                          }
                          question_options={question.question_options}
                          error={!!error}
                          organizationId={organizationId || 0}
                          preview={preview}
                        />
                      </FormControl>
                      {error && <FormMessage>{error.message}</FormMessage>}
                    </FormItem>
                  )}
                />
              ))}
            <div className="tw-flex  tw-justify-center tw-mt-5.6 tw-mb-7.2">
              <Button
                size="lg"
                type="submit"
                disabled={!form.formState.isValid || loading || preview}
                loading={loading}
                loadingText={t('LOADING_SEND_APPLICATION')}
              >
                {t('SEND_APPLICATION')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
      <EmailValidationPopUp
        email={form.getValues('email')}
        open={openEmailValidation}
        onOpenChange={setOpenEmailValidation}
      />
      <LinkedInValidationPopUp
        linkedinUrl={form.getValues('linkedin_url') || ''}
        open={openLinkedInValidation}
        onOpenChange={setOpenLinkedInValidation}
      />
    </div>
  );
};
